import { useQuery } from "@tanstack/react-query";
import type { WorkItemTraceability } from "@zworks/console-contracts";
import * as React from "react";
import { FileText, CheckCircle2, XCircle, Clock, AlertCircle } from "lucide-react";
import { PageHeader } from "@/components/bespoke";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCurrentProject } from "@/lib/project";
import { traceabilityQuery } from "@/lib/api";
import { exportTraceabilityToCSV } from "@/lib/traceability-csv";
import { TraceabilityReport } from "@/components/features/traceability/traceability-report";

/** Empty state component */
function EmptyTraceability() {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-center space-y-3">
          <div className="text-muted-foreground">No work items found for this project</div>
          <div className="text-sm text-muted-foreground">
            Work items traceability shows mapping from requirements to test cases and results.
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Summary cards */
function TraceabilitySummary({ data }: { data: WorkItemTraceability[] }) {
  const total = data.length;
  const covered = data.filter((item) => item.coverageStatus === "covered").length;
  const failing = data.filter((item) => item.coverageStatus === "failing").length;
  const blocked = data.filter((item) => item.coverageStatus === "blocked").length;
  const _untested = data.filter((item) => item.coverageStatus === "untested").length;
  const noTests = data.filter((item) => item.coverageStatus === "no_tests").length;

  const coverageRate = total > 0 ? Math.round((covered / total) * 100) : 0;

  return (
    <div className="grid gap-4 md:grid-cols-5">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Total Work Items</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{total}</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Covered</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-success">{covered}</div>
          <div className="text-xs text-muted-foreground">
            {total > 0 ? `${coverageRate}% coverage` : "0% coverage"}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Failing</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-destructive-text">{failing}</div>
          <div className="text-xs text-muted-foreground">Need attention</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Blocked</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-warning">{blocked}</div>
          <div className="text-xs text-muted-foreground">Dependencies</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">No Tests</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-warning">{noTests}</div>
          <div className="text-xs text-muted-foreground">Flagged for test creation</div>
        </CardContent>
      </Card>
    </div>
  );
}

/** Main traceability page */
export function TraceabilityPage() {
  const { project, loaded } = useCurrentProject();
  const projectId = project?.id;

  const {
    data: traceabilityData,
    isLoading,
    error,
    refetch,
  } = useQuery(
    projectId && loaded
      ? traceabilityQuery(projectId)
      : {
          queryKey: ["projects", projectId ?? "", "quality", "traceability"],
          queryFn: () => Promise.resolve({ items: [] } as { items: WorkItemTraceability[] }),
          enabled: false,
        },
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading traceability data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Failed to load traceability data: {error.message}</AlertDescription>
      </Alert>
    );
  }

  const data = traceabilityData?.items ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Traceability Report"
        description="Requirements traceability and coverage report - linking work items to test cases and results"
        actions={
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50">
                <FileText className="h-4 w-4" />
                Export CSV
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => exportTraceabilityToCSV(data)}>
                  Export all ({data.length} items)
                </DropdownMenuItem>
                <DropdownMenuItem disabled>Export filtered (coming soon)</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Refresh
            </Button>
          </div>
        }
      />

      {data.length > 0 && <TraceabilitySummary data={data} />}

      {data.length === 0 ? <EmptyTraceability /> : <TraceabilityReport items={data} />}

      {/* Legend for coverage statuses */}
      {data.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Coverage Status Legend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 text-sm md:grid-cols-2 lg:grid-cols-5">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-success" />
                <span>Covered: All tests pass</span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="h-3 w-3 text-destructive-text" />
                <span>Failing: Some tests fail</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3 text-warning" />
                <span>Blocked: Tests blocked due to dependencies</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3 text-info" />
                <span>Untested: Tests exist but haven't run</span>
              </div>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-3 w-3 text-warning" />
                <span>No tests: No test cases linked to this work item</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
